import { translate } from "@/i18n";
import { desktopVoiceMessages } from "@/i18n/messages/desktop-voice";

/** Speech held at least this long (at probability 0.5) is the user's voice. */
export const SPEECH_START_MS = 150;

/** Audio frames and successful local inference are separate readiness evidence. */
export class ContinuousVoiceActivityHealth {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private generation = 0;
  private onFailure: ((message: string) => void) | null = null;
  private ready = false;
  private speechSince: number | null = null;
  private lastSpeech: number | null = null;
  private speechDurationMs = 0;
  private restarting = false;

  start(onFailure: (message: string) => void): void {
    this.stop();
    this.onFailure = onFailure;
    this.arm(
      10_000,
      translate(desktopVoiceMessages, "errorDetectionNotStarted"),
    );
  }

  accept(
    probability: number | null,
    error?: string,
    audioDurationMs?: number,
  ): boolean {
    if (!this.onFailure) return false;
    if (error) {
      this.fail(translate(desktopVoiceMessages, "errorDetectionFailed"));
      return false;
    }
    if (probability === null) return false;
    if (
      audioDurationMs !== undefined &&
      (!Number.isFinite(audioDurationMs) ||
        audioDurationMs <= 0 ||
        audioDurationMs > 1024)
    )
      return false;
    if (probability >= 0.5) {
      this.speechSince ??= Date.now();
      this.speechDurationMs += audioDurationMs ?? 0;
      if (
        Math.max(Date.now() - this.speechSince, this.speechDurationMs) >=
        SPEECH_START_MS
      )
        this.lastSpeech = Date.now();
    } else {
      this.speechSince = null;
      this.speechDurationMs = 0;
    }
    const first = !this.ready;
    this.ready = true;
    if (!this.restarting) this.armReceiving();
    return first;
  }

  /** The capture is reopening its microphone: no audio is expected until it
   *  reports back, but a restart that never finishes still fails. */
  suspendForRestart(): void {
    if (!this.onFailure) return;
    this.restarting = true;
    this.arm(10_000, translate(desktopVoiceMessages, "errorMicNoRestart"));
  }

  resumeAfterRestart(): void {
    if (!this.onFailure) return;
    this.restarting = false;
    if (this.ready) this.armReceiving();
    else
      this.arm(
        10_000,
        translate(desktopVoiceMessages, "errorDetectionNotStarted"),
      );
  }

  isReady(): boolean {
    return this.ready;
  }

  hasRecentSpeech(): boolean {
    return this.lastSpeech !== null && Date.now() - this.lastSpeech < 3_000;
  }

  /** When speech was last heard, or null when none was this capture. */
  lastSpeechAt(): number | null {
    return this.lastSpeech;
  }

  stop(): void {
    this.generation += 1;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.onFailure = null;
    this.ready = false;
    this.restarting = false;
    this.speechSince = null;
    this.lastSpeech = null;
    this.speechDurationMs = 0;
  }

  private fail(message: string): void {
    const callback = this.onFailure;
    this.stop();
    callback?.(message);
  }

  private armReceiving(): void {
    this.arm(3_000, translate(desktopVoiceMessages, "errorDetectionNoAudio"));
  }

  private arm(delay: number, message: string): void {
    if (this.timer !== null) clearTimeout(this.timer);
    const generation = this.generation;
    this.timer = setTimeout(() => {
      if (generation === this.generation) this.fail(message);
    }, delay);
  }
}

export const continuousVoiceActivityHealth =
  new ContinuousVoiceActivityHealth();

/** A finished segment whose transcript never arrived: the user restarts. */
export const transcriptionTimeoutError = () =>
  translate(desktopVoiceMessages, "errorTranscriptionTimeout");
